/**
 * Prowizje handlowców z InsERT (dodatek prowizyjny) i retro przy kliencie (D15) — klucze `com.*`.
 * Scalane w słownik przez `extra.ts`; `comEn` jest typowane względem `comPl`, więc brak klucza = błąd kompilacji.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const comPl = {
  nav: 'Prowizje',
  title: 'Prowizje handlowców',
  subtitle: 'Prowizje z dodatku prowizyjnego InsERT: naliczone od zapłaconych dokumentów, potrącenia za spóźnione płatności i kwota do wypłaty.',
  source: {
    ERP: 'Źródło: InsERT — dodatek prowizyjny',
    DEMO: 'Dane przykładowe',
    demoHint: 'Dodatek prowizyjny w ERP nie ma bieżących wypłat — pokazujemy prowizje przykładowe policzone na prawdziwej sprzedaży.',
  },
  filters: { from: 'Od miesiąca', to: 'Do miesiąca', rep: 'Handlowiec', allReps: 'Wszyscy handlowcy' },
  kpi: {
    accrued: 'Naliczona',
    accruedHint: 'Suma prowizji z pozycji dokumentów',
    penalty: 'Potrącenia za spóźnienia',
    penaltyHint: 'Wg progów dni spóźnienia handlowca',
    adjustments: 'Korekty',
    adjustmentsHint: 'Częściowe zapłaty i zaokrąglenia ERP',
    payable: 'Do wypłaty',
    payableHint: '{n} wypłat w okresie',
  },
  table: {
    title: 'Prowizja per handlowiec i miesiąc',
    subtitle: 'Kliknij wiersz, aby zobaczyć dokumenty i grupy towarów ze stawkami.',
    month: 'Miesiąc',
    rep: 'Handlowiec',
    net: 'Netto objęte',
    accrued: 'Naliczona',
    penalty: 'Potrącenia',
    adjustments: 'Korekty',
    payable: 'Do wypłaty',
    payouts: 'Wypłaty',
    total: 'Razem',
    unlinked: 'bez konta NEXOPS',
    empty: 'Brak prowizji w wybranym okresie.',
  },
  payouts: {
    title: 'Wypłaty prowizji',
    subtitle: '{rep} · {month}',
    document: 'Dokument',
    customer: 'Klient',
    paid: 'Zapłata',
    due: 'Termin',
    daysLate: 'Dni spóźnienia',
    late: '{days} dni po terminie',
    onTime: 'w terminie',
    accrued: 'Naliczona',
    penalty: 'Potrącenie',
    payable: 'Do wypłaty',
    more: 'Pokazano {shown} z {total}',
    showMore: 'Pokaż więcej',
    linesTitle: 'Grupy towarów ze stawkami',
    group: 'Grupa towarów',
    noGroup: 'bez grupy',
    lines: 'Pozycje',
    net: 'Netto',
    rate: 'Stawka',
    effective: 'Efektywnie',
    commission: 'Prowizja',
    rates: 'Stawki i progi handlowca',
    defaultRate: 'stawka domyślna',
    lateRules: 'Potrącenia za spóźnienia',
    lateRule: '{from}–{to} dni: {pct}',
    lateRuleOpen: 'od {from} dni: {pct}',
    noDeduction: 'bez potrącenia',
  },
  bonus: {
    title: 'Prowizje z ERP w tym miesiącu',
    titleDemo: 'Prowizje (dane przykładowe) w tym miesiącu',
    subtitle: 'Prowizja to rzeczywista należność z dodatku prowizyjnego — symulacja premii poniżej jej nie zmienia.',
    empty: 'Brak prowizji w tym miesiącu. Ostatni miesiąc z prowizjami: {month}.',
    open: 'Otwórz Prowizje',
    mine: 'Twoja prowizja w tym miesiącu',
  },
  retro: {
    tab: 'Retro',
    title: 'Retro przy kliencie',
    subtitle: 'Widoczne wyłącznie dla zarządu i administratora. Procent od obrotu netto albo kwota miesięcznie.',
    add: 'Dodaj retro',
    edit: 'Edytuj retro',
    remove: 'Usuń',
    removeConfirm: 'Usunąć to retro?',
    save: 'Zapisz',
    cancel: 'Anuluj',
    empty: 'Brak retro przy tym kliencie.',
    kind: { PERCENT: 'Procent od obrotu', AMOUNT: 'Kwota miesięcznie' },
    type: { OFFICIAL: 'Oficjalne', UNOFFICIAL: 'Nieoficjalne' },
    field: { kind: 'Rodzaj naliczenia', value: 'Wartość', valuePct: 'Procent (%)', valueAmount: 'Kwota miesięcznie (zł netto)', from: 'Obowiązuje od', to: 'Obowiązuje do', toHint: 'puste = bezterminowo', type: 'Rodzaj umowy', note: 'Notatka' },
    period: '{from} – {to}',
    openEnded: 'od {from}, bezterminowo',
    demo: 'dane przykładowe',
    fromErp: 'z Subiektu',
    margin: {
      title: 'Marża po retro',
      window: 'Ostatnie 12 miesięcy i bieżący: {from} – {to}',
      net: 'Sprzedaż netto',
      margin: 'Marża',
      retro: 'Retro',
      unofficial: 'w tym nieoficjalne {value}',
      after: 'Marża po retro',
    },
    abc: { retro: 'Retro', after: 'Marża po retro' },
    errors: { save: 'Nie udało się zapisać retro.' },
  },
};

export const comEn: Shape<typeof comPl> = {
  nav: 'Commissions',
  title: 'Sales commissions',
  subtitle: 'Commissions from the InsERT commission add-on: accrued on paid documents, late-payment deductions and the amount to pay.',
  source: {
    ERP: 'Source: InsERT commission add-on',
    DEMO: 'Sample data',
    demoHint: 'The ERP commission add-on has no current payouts — sample commissions computed on the real sales are shown.',
  },
  filters: { from: 'From month', to: 'To month', rep: 'Sales rep', allReps: 'All sales reps' },
  kpi: {
    accrued: 'Accrued',
    accruedHint: 'Sum of the commissions of the document lines',
    penalty: 'Late-payment deductions',
    penaltyHint: 'By the rep’s days-late brackets',
    adjustments: 'Adjustments',
    adjustmentsHint: 'Partial payments and ERP rounding',
    payable: 'To pay',
    payableHint: '{n} payouts in the period',
  },
  table: {
    title: 'Commission per rep and month',
    subtitle: 'Click a row to see the documents and product groups with rates.',
    month: 'Month',
    rep: 'Sales rep',
    net: 'Net covered',
    accrued: 'Accrued',
    penalty: 'Deductions',
    adjustments: 'Adjustments',
    payable: 'To pay',
    payouts: 'Payouts',
    total: 'Total',
    unlinked: 'no NEXOPS account',
    empty: 'No commissions in the selected period.',
  },
  payouts: {
    title: 'Commission payouts',
    subtitle: '{rep} · {month}',
    document: 'Document',
    customer: 'Customer',
    paid: 'Paid',
    due: 'Due',
    daysLate: 'Days late',
    late: '{days} days late',
    onTime: 'on time',
    accrued: 'Accrued',
    penalty: 'Deduction',
    payable: 'To pay',
    more: 'Showing {shown} of {total}',
    showMore: 'Show more',
    linesTitle: 'Product groups with rates',
    group: 'Product group',
    noGroup: 'no group',
    lines: 'Lines',
    net: 'Net',
    rate: 'Rate',
    effective: 'Effective',
    commission: 'Commission',
    rates: 'Rates and brackets of the rep',
    defaultRate: 'default rate',
    lateRules: 'Late-payment deductions',
    lateRule: '{from}–{to} days: {pct}',
    lateRuleOpen: 'from {from} days: {pct}',
    noDeduction: 'no deduction',
  },
  bonus: {
    title: 'ERP commissions this month',
    titleDemo: 'Commissions (sample data) this month',
    subtitle: 'The commission is the actual amount due from the commission add-on — the bonus simulation below does not change it.',
    empty: 'No commissions this month. Latest month with commissions: {month}.',
    open: 'Open Commissions',
    mine: 'Your commission this month',
  },
  retro: {
    tab: 'Retro',
    title: 'Customer retro',
    subtitle: 'Visible to the board and the administrator only. Percent of the net turnover or an amount per month.',
    add: 'Add retro',
    edit: 'Edit retro',
    remove: 'Delete',
    removeConfirm: 'Delete this retro?',
    save: 'Save',
    cancel: 'Cancel',
    empty: 'No retro for this customer.',
    kind: { PERCENT: 'Percent of turnover', AMOUNT: 'Amount per month' },
    type: { OFFICIAL: 'Official', UNOFFICIAL: 'Unofficial' },
    field: { kind: 'Calculation', value: 'Value', valuePct: 'Percent (%)', valueAmount: 'Amount per month (PLN net)', from: 'Valid from', to: 'Valid to', toHint: 'empty = open-ended', type: 'Agreement type', note: 'Note' },
    period: '{from} – {to}',
    openEnded: 'from {from}, open-ended',
    demo: 'sample data',
    fromErp: 'from Subiekt',
    margin: {
      title: 'Margin after retro',
      window: 'Last 12 months and the current one: {from} – {to}',
      net: 'Net sales',
      margin: 'Margin',
      retro: 'Retro',
      unofficial: 'incl. unofficial {value}',
      after: 'Margin after retro',
    },
    abc: { retro: 'Retro', after: 'Margin after retro' },
    errors: { save: 'Could not save the retro.' },
  },
};
